/**
 * How much of a record this device keeps, and which rows go.
 *
 * The mirror used to be everything the server had. That is what makes the app
 * fast, and it is also what stops it being fast: half a million rows is a
 * database file, five indexes and an FTS5 document per row on a phone, and the
 * version chain grows the count with the EDIT rate rather than the entry rate.
 *
 * So a record keeps a window, and the server keeps the history. The window is
 * the SMALLER of two answers, which is the rule this file exists to state:
 *
 *   the duration    everything from `MIRROR_WINDOW_DAYS` ago
 *   the cap         the newest `MIRROR_ROW_CAP` rows
 *
 * The later of the two floor days wins, because a later floor keeps fewer rows.
 * A quiet record never reaches the cap and keeps its ninety days; a record
 * taking four hundred rows a day reaches the cap in a week and keeps a week.
 *
 * **Whole days only.** A cap that fell inside a day would leave that day half
 * mirrored, and a card asking for it would answer with half the money and no
 * sign that it had. `listEntriesPage` already ends a page on a whole day for
 * the same reason. So the cap's floor is the DAY of the capth row, and every
 * row of that day stays.
 *
 * **Nothing goes unless a bucket covers it.** A deleted row is only safe
 * because `aggregate_bucket` can still answer for it, so the delete tests for
 * that bucket row by row. A day the server has not bucketed is a day this
 * device keeps, whatever its age. This is the one interlock that makes the
 * prune recoverable rather than a loss.
 *
 * Pure by construction: statements and decisions here, the connection in
 * `entry-repo.ts`, so `db-migrations.sqlite.test.ts` runs the real statements
 * against the real migrations.
 */

/** How far back a record keeps rows, in days, today included. */
export const MIRROR_WINDOW_DAYS = 90;

/**
 * How many rows a record keeps at most, whatever the window says.
 *
 * One number, and the one to change when a phone turns out to hold more or
 * less than this comfortably. It is a CAP and not a target: a record with fewer
 * rows than this keeps all of them.
 */
export const MIRROR_ROW_CAP = 1000;

/** How many rows one delete statement takes, so the JavaScript thread is never held. */
export const PRUNE_CHUNK = 250;

/**
 * The oldest day a first download asks the server for.
 *
 * The duration half of the rule, and only that half: the cap is a count, and
 * the server cannot know how many rows will come back before it sends them. A
 * fill therefore brings ninety days and the prune takes it down to the cap
 * afterwards, which costs one download of rows that are deleted on arrival and
 * keeps the server's side of this to one comparison.
 */
export const mirrorFloorDay = (today: string): string => {
  const at = Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)));
  return new Date(at - MIRROR_WINDOW_DAYS * 86_400_000).toISOString().slice(0, 10);
};

/** A statement and what it binds, in the order the `?` appear. */
export type WindowStatement = { sql: string; params: (string | number)[] };

/**
 * The oldest day a record may keep: the later of the duration floor and the
 * cap floor.
 *
 * `OFFSET` is the cap less one, so the row it names is the capth newest and its
 * whole day is kept. A record with fewer rows than the cap has no capth row,
 * the union contributes one value, and the duration floor stands alone.
 *
 * Answers one row with one column, `floor`, which is never null: the duration
 * floor is always there.
 */
export const floorStatement = (recordId: string, today: string): WindowStatement => ({
  // The capth day is wrapped in its own SELECT, and that is not decoration.
  // SQLite binds an ORDER BY, a LIMIT and an OFFSET written on an arm of a
  // UNION to the WHOLE compound select, so the unwrapped form answered the
  // thousandth row of the two arms together — which is no row at all on a
  // record below the cap, and a null floor there means the ninety-day rule
  // would never prune anything.
  sql: `
    SELECT MAX(d) AS floor FROM (
      SELECT date(?, ?) AS d
      UNION ALL
      SELECT d FROM (
        SELECT day AS d FROM entry_mirror
         WHERE record_id = ? AND deleted_at IS NULL AND day IS NOT NULL
         ORDER BY day DESC LIMIT 1 OFFSET ?)
    )`,
  params: [today, `-${MIRROR_WINDOW_DAYS} days`, recordId, MIRROR_ROW_CAP - 1],
});

/**
 * The next chunk of rows to forget: older than the floor, already accepted by
 * the server, and covered by a bucket.
 *
 * Four conditions, and every one of them is load bearing.
 *
 *   day < floor        what the window says
 *   pending = 0        a row the server has never seen has no bucket and no
 *                      second copy anywhere. It stays until it is pushed.
 *   day IS NOT NULL    a row with no day belongs to no bucket
 *   a bucket exists    the interlock. Nothing is deleted that nothing can
 *                      answer for.
 *
 * The outbox is tested too, although `pending` already covers it today: the two
 * are set together by `writePending`, and a belt beside the braces costs one
 * lookup on a table that holds a handful of rows.
 */
export const doomedStatement = (
  recordId: string,
  floorDay: string,
  /**
   * The JSON path of the record's time axis: `$."<fieldId>"`.
   *
   * A row whose date field is EMPTY has no bucket — the server writes one only
   * when the time axis reads as a day — but it does have a day on this device,
   * because `dayOf` falls back to the row's creation time. Left alone it would
   * be deleted on a day its neighbours had bucketed, with nothing able to
   * answer for it. So a row only goes when its own date is filled in.
   */
  axisPath: string,
): WindowStatement => ({
  sql: `
    SELECT entry_id AS entryId FROM entry_mirror m
     WHERE m.record_id = ?
       AND m.pending = 0
       AND m.day IS NOT NULL
       AND m.day < ?
       AND json_extract(m.values_json, ?) IS NOT NULL
       AND EXISTS (SELECT 1 FROM aggregate_bucket b WHERE b.record_id = m.record_id AND b.day = m.day)
       AND m.entry_id NOT IN (SELECT entry_id FROM outbox)
     LIMIT ?`,
  params: [recordId, floorDay, axisPath, PRUNE_CHUNK],
});

/**
 * The later of two floors, either of which may be absent.
 *
 * A record's horizon comes from two places and the truth is the later one: the
 * day a windowed first download asked the server for, and the day a prune cut
 * to. Neither alone is the answer — a download can window rows away that a
 * prune would then find nothing to delete below, which is exactly how a record
 * under the row cap ends up holding ninety days and claiming to hold all of
 * them.
 */
export const laterFloor = (left: string | null, right: string | null): string | null => {
  if (left === null) return right;
  if (right === null) return left;
  return left > right ? left : right;
};

/**
 * Whether this record may be pruned at all, given its cached schema.
 *
 * Two records are never pruned, and both exemptions are about a screen that
 * would otherwise lie.
 *
 * **A record with no time axis.** Its rows have no day of their own, so the
 * server writes no bucket for them, and `dayOf` on the device falls back to the
 * row's creation time — a day the server has never agreed to. Pruning by a day
 * the two sides do not share is how rows vanish with nothing to replace them.
 *
 * **A list another record points AT** (`pointedRecords`). A directory's
 * reference picker reads the whole of it: the name a person is choosing from is
 * not history, and the server's own search door cannot page a list, so a partly
 * mirrored directory is a picker that cannot find a worker who is standing in
 * front of you. Directories are small — hundreds, not lakhs — so keeping them
 * whole costs nothing worth having. A record a pick targets (a Guest Service
 * Order picks its Booking) is windowed like any other since step 9.8 of
 * docs/plans/field-relations.md: the pull sends the title of each picked entry
 * it leaves out (`pickTitles`), the device keeps it (`pick_title`), and a pick
 * of a pruned booking is named from there.
 */
export const isPrunable = (
  recordId: string,
  hasTimeAxis: boolean,
  referencedRecordIds: ReadonlySet<string>,
): boolean => hasTimeAxis && !referencedRecordIds.has(recordId);

/** One cached schema as `pointedRecords` reads it: the fields, their bindings, and the lines below. */
export type PointingSchema = {
  readonly fields: readonly { readonly id: string; readonly targetRecordId?: string | null }[];
  readonly bindings: Readonly<Partial<Record<string, { readonly fieldId: string } | { readonly constNum: number }>>>;
  readonly lineSchemas?: Readonly<Record<string, PointingSchema>>;
};

/**
 * The records some cached schema points AT: the ones `isPrunable` keeps whole.
 * The server's rule (`timeAxisCase` in apps/server/src/routes/sync.ts), so the
 * two sides agree on which records are windowed:
 *
 * - a list a field points at is exempt; a record a pick targets is not
 *   (`isRecord` says which is which), since its titles travel on their own.
 * - a lines record's parent and root point up at the entry above, not at a
 *   record anybody picks from, so they exempt nothing.
 * - a field on a line points as a field on the record does.
 */
export const pointedRecords = (schemas: readonly PointingSchema[], isRecord: (recordId: string) => boolean = () => false): Set<string> => {
  const out = new Set<string>();
  const walk = (schema: PointingSchema): void => {
    const tree = new Set(
      (['parent', 'root'] as const).flatMap((purpose) => {
        const bound = schema.bindings[purpose];
        return bound !== undefined && 'fieldId' in bound ? [bound.fieldId] : [];
      }),
    );
    for (const field of schema.fields) {
      const target = field.targetRecordId;
      if (target === null || target === undefined || tree.has(field.id) || isRecord(target)) continue;
      out.add(target);
    }
    for (const below of Object.values(schema.lineSchemas ?? {})) walk(below);
  };
  for (const schema of schemas) walk(schema);
  return out;
};
